import { useStore } from '../../app/store';
import { fetchLinkedPeopleOrNull } from './session';
import { hydrateLearnerProgress } from './hydrateLearnerProgress';

/**
 * Projecteert de gekoppelde kinderen van een OUDER naar de lokale accountstaat.
 *
 * ── Waarom dit bestaat ────────────────────────────────────────────────────
 *
 * De koppeling staat in `parent_links` op de server. De kinderlijst, de
 * kindwisselaar en de "Vergelijken"-pagina lezen `multiAccountState`. Zonder
 * deze projectie kent het ouderportaal zijn eigen kinderen niet.
 *
 * Dit stond eerst als effect in App.tsx, gebonden aan `[session]` — en dus
 * alleen bij het inloggen. Koppelen verandert de sessie niet, dus een tweede
 * kind verscheen wél in het toestemmingspaneel (dat leest rechtstreeks de
 * server) en niet in de lijst. Door het uit het effect te halen kan het
 * aangeroepen worden op elk moment dat de koppeling verandert.
 *
 * ── Faalstand ─────────────────────────────────────────────────────────────
 *
 * Bij een mislukte serveraanroep gebeurt er NIETS. Dat is de reden dat hier
 * `fetchLinkedPeopleOrNull()` staat en niet `fetchLinkedPeople()`: die tweede
 * maakt van een fout een lege lijst, en hieronder wordt op grond van die lijst
 * ook opgeruimd. Een haperende wifi zou dan alle koppelingen wissen.
 *
 * De returnwaarde is `false` wanneer de lijst niet kon worden vastgesteld.
 * Dat is nodig omdat "geen kinderen" en "niet kunnen kijken" in het portaal tot
 * hetzelfde scherm leidden: het koppelformulier, met de mededeling dat er nog
 * geen kind gekoppeld is. Voor een ouder die er wél één heeft, is dat een
 * onware bewering — en het stuurt hem naar een code die hij niet meer heeft.
 */
export async function syncLinkedChildren(parentId: string): Promise<boolean> {
  const people = await fetchLinkedPeopleOrNull();
  if (people === null) return false; // Serverfout — niets aannemen, niets wijzigen.
  const children = people.filter(p => p.relation === 'child').map(p => ({
    id: p.id,
    displayName: p.name
  }));
  const s = useStore.getState();
  const accts = s.multiAccountState?.accounts;
  if (!s.multiAccountState || !accts) return false;

  // Rechtstreeks opbouwen in plaats van via `applyProfileToAccounts`: die
  // functie zet elk profiel als ACTIEF account, en voor kinderen klopt dat
  // niet — de ouder blijft ingelogd.
  const now = Date.now();
  const byId: Record<string, unknown> = {
    ...accts.accountsById
  };
  for (const child of children) {
    byId[child.id] = {
      ...(byId[child.id] as object ?? {}),
      id: child.id,
      role: 'learner',
      displayName: child.displayName ?? 'Leerling',
      username: child.id,
      createdAt: now,
      lastActive: now,
      hasCompletedOnboarding: true
    };
  }
  const rel = accts.relationships ?? {};
  const childIds = children.map(c => c.id);

  /*
   ── Opruimen: kinderen die de server niet meer noemt ────────────────────

   De projectie voegde alleen toe. Werd een koppeling elders verbroken — door
   de andere ouder, door het kind, of doordat een beheerder een onterechte
   koppeling weghaalde — dan bleef dit kind hier staan tot de volgende keer
   inloggen. Het ouderportaal toonde dus een kind waar deze ouder niet meer
   bij mocht, inclusief zijn voortgang uit de lokale kopie.

   Alleen de RELATIE gaat weg, niet het account en niet `learnerData`. Op een
   gezinsapparaat oefent dit kind hier zelf; zijn account en voortgang zijn
   niet van de ouder om op te ruimen. Wat wegvalt is de aanspraak van deze
   ouder erop — en dat is precies wat er serverzijdig ook is gebeurd.
   */
  const learnerToParents: Record<string, string[]> = {
    ...(rel.learnerToParents ?? {})
  };
  const previous = rel.parentToLearners?.[parentId] ?? [];
  for (const goneId of previous.filter(id => !childIds.includes(id))) {
    const remaining = (learnerToParents[goneId] ?? []).filter(pid => pid !== parentId);
    if (remaining.length > 0) learnerToParents[goneId] = remaining;else delete learnerToParents[goneId];
  }
  for (const child of children) {
    const current = learnerToParents[child.id] ?? [];
    learnerToParents[child.id] = current.includes(parentId) ? current : [...current, parentId];
  }
  useStore.setState({
    multiAccountState: {
      ...s.multiAccountState,
      accounts: {
        ...accts,
        accountsById: byId,
        activeAccountId: parentId,
        relationships: {
          ...rel,
          parentToLearners: {
            ...(rel.parentToLearners ?? {}),
            [parentId]: childIds
          },
          learnerToParents,
          teacherToLearners: rel.teacherToLearners ?? {}
        }
      }
    }
  });
  if (children.length === 0) return true;

  // ── En dan de voortgang zelf ──────────────────────────────────────
  // Hierboven is alleen bekend geworden DAT deze kinderen bestaan. Hun
  // mastery, XP en streak staan in `solo_progress` op de server — niet op het
  // apparaat van de ouder. Zonder deze stap toont het portaal een kind met een
  // lege voortgang, wat er uitziet als een kind dat nooit heeft geoefend.
  //
  // Bewust ná de accountprojectie en in een eigen stap: mislukt dit door een
  // netwerkfout, dan ziet de ouder zijn kinderen nog wél staan.
  await hydrateLearnerProgress(childIds);
  return true;
}
